import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_NAME, SITE_URL } from "@/components/base/Seo";
import PlanFaq from "@/pages/plans/components/PlanFaq";
import { planFaqs } from "@/mocks/plans";

export default function FaqsPage() {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: planFaqs.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: { "@type": "Answer", text: faq.answer },
    })),
  };

  return (
    <SiteLayout>
      <Seo
        title="Frequently Asked Questions"
        description="Answers to common questions about listing your business on Coastal Bizz Connect — free plans, yearly billing, upgrades, payment methods, renewals and multi-location plans."
        keywords="Coastal Bizz Connect FAQ, business listing questions, listing plans help, Karnataka business directory support"
        path="/faqs"
        jsonLd={[
          faqJsonLd,
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Frequently Asked Questions",
            url: `${SITE_URL}/faqs`,
            isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
          },
        ]}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">FAQs</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            Frequently asked questions
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            Everything you need to know about plans, payments and renewals on Coastal Bizz Connect.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-16">
        <PlanFaq />
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-start gap-5 px-6 py-10 md:flex-row md:items-center md:justify-between md:py-12">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              Still have a question?
            </h2>
            <p className="mt-1.5 max-w-xl text-sm text-foreground-600">
              Our team is happy to help with anything about listings, plans or your account.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/contact"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors duration-200 hover:bg-background-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-mail-line text-base" />
              </span>
              Contact us
            </Link>
            <Link
              to="/listing-plans"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-accent-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-price-tag-3-line text-base" />
              </span>
              View listing plans
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}